'use client';
import { useState, useCallback } from 'react';
import { useTranslations as useTranslations$1 } from 'next-intl';
import { Link, usePathname } from '@/i18n/navigation';
import { Menu, X, ArrowRight } from '@/components/ui/icons';
import { useRipple } from '@/hooks/useRipple';
import { useAuth } from '@/hooks/useAuth';
import { useStoredSignupAccess } from '@/hooks/useStoredSignupAccess';
import { SignUpModal } from '@/components/auth';
import { hasStoredSignupAccess } from '@/lib/signupAccessStorage';
import { startDevLogin } from '@/lib/devLogin';
import { DOCS_HOME } from '@/lib/docs';
import { BAR_START_PATH } from '@/app/[locale]/(marketing)/bar/BarAccessCta';
import { isByokPlansEnabled } from '@/lib/byokPlansFlag';
interface NavItem {
    label: string;
    href: string;
    isSection?: boolean;
    /** Plain <a>: an (app) route with no locale prefix (see MarketingNav). */ unlocalized?: boolean;
}
export function MobileNav() {
    const t = useTranslations$1();
    const [isOpen, setIsOpen] = useState(false);
    const pathname = usePathname();
    const isHomePage = pathname === '/';
    // On /bar the primary CTA opens fal setup once the fal-key plans are on; until then it is the usual signup.
    const isBarEntry = pathname === '/bar' && isByokPlansEnabled();
    const { createRipple, rippleElements } = useRipple();
    const { isAnonymous, isLoading } = useAuth();
    const isLoggedIn = !isAnonymous && !isLoading;
    const [showSignUpModal, setShowSignUpModal] = useState(false);
    const [modalMode, setModalMode] = useState<'signup' | 'signin'>('signin');
    const hasSignupAccess = useStoredSignupAccess();
    const navItems: NavItem[] = [
        {
            label: t("ZXp0z1"),
            href: '#production',
            isSection: true
        },
        {
            label: t("wNpRl5"),
            href: '#showcase',
            isSection: true
        },
        // i18n-ignore -- product name
        {
            label: 'Martini Bar',
            href: '/bar'
        },
        {
            label: t("5Wwopw"),
            href: '/pricing'
        },
        {
            label: t("_6_sPX"),
            href: DOCS_HOME,
            unlocalized: true
        }
    ];
    const handleSectionClick = useCallback((href: string)=>{
        if (isHomePage) {
            const sectionId = href.replace('#', '');
            const element = document.getElementById(sectionId);
            if (element) {
                // Account for the navbar height (h-20 = 80px) plus some padding
                const navbarHeight = 64;
                const offset = navbarHeight + 4 // 16px extra padding
                ;
                const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
                const offsetPosition = elementPosition - offset;
                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                });
            }
        }
        setIsOpen(false);
    }, [
        isHomePage
    ]);
    const handleLinkClick = ()=>{
        setIsOpen(false);
    };
    const handleDemoClick = (e: React.MouseEvent<HTMLButtonElement>)=>{
        createRipple(e);
        handleLinkClick();
        // The Bar's own way in: straight into fal setup, which brings its own signup modal.
        if (isBarEntry) {
            window.location.href = BAR_START_PATH;
            return;
        }
        if (hasStoredSignupAccess()) {
            setModalMode('signup');
            setShowSignUpModal(true);
        } else {
            window.open('https://form.typeform.com/to/VVwyeUrj', '_blank', 'noopener,noreferrer');
        }
    };
    // Same rule as MarketingNav: sign-in from /pricing returns there, query included.
    const [returnPath, setReturnPath] = useState('/projects');
    const handleLoginClick = async ()=>{
        handleLinkClick();
        const back = pathname === '/pricing' ? `${pathname}${window.location.search}` : '/projects';
        setReturnPath(back);
        if (await startDevLogin(back)) return;
        setModalMode('signin');
        setShowSignUpModal(true);
    };
    return (<div className="lg:hidden">
      { /* Hamburger Button */ }
      <button onClick={()=>setIsOpen(!isOpen)} className="fixed end-6 top-6 z-50 flex items-center justify-center" aria-label={isOpen ? 'Close menu' : 'Open menu'} aria-expanded={isOpen}>
        {isOpen ? <X className="h-5 w-5 text-white"/> : <Menu className="h-5 w-5 text-white"/>}
      </button>

      { /* Mobile Menu Overlay */ }
      <div className={`fixed inset-0 z-40 bg-black/90 backdrop-blur-xl transition-all duration-300 ${isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'}`} onClick={()=>setIsOpen(false)}>
        { /* Menu Content */ }
        <nav className={`absolute end-0 top-0 flex h-full w-full max-w-sm flex-col p-8 transition-transform duration-300 ${isOpen ? 'translate-x-0' : 'ltr:translate-x-full rtl:-translate-x-full'}`} onClick={(e)=>e.stopPropagation()}>
          <ul className="mt-24 space-y-6">
            {navItems.map((item)=>(<li key={item.href}>
                {item.isSection ? (isHomePage ? (<button onClick={()=>handleSectionClick(item.href)} className="block w-full text-start text-2xl font-medium text-white/70 transition-colors duration-200 hover:text-white">
                      {item.label}
                    </button>) : (<Link href={`/${item.href}`} onClick={handleLinkClick} className="block text-2xl font-medium text-white/70 transition-colors duration-200 hover:text-white">
                      {item.label}
                    </Link>)) : item.unlocalized ? (<a href={item.href} onClick={handleLinkClick} className="block text-2xl font-medium text-white/70 transition-colors duration-200 hover:text-white">
                    {item.label}
                  </a>) : (<Link href={item.href} onClick={handleLinkClick} className="block text-2xl font-medium text-white/70 transition-colors duration-200 hover:text-white">
                    {item.label}
                  </Link>)}
              </li>))}

            {!isLoggedIn && (<li>
                <button data-cta="log_in" onClick={handleLoginClick} className="block w-full text-start text-2xl font-medium text-white/70 transition-colors duration-200 hover:text-white">
                  {t("odXlk8")}
                </button>
              </li>)}
          </ul>

          { /* Primary CTA Button */ }
          <div className="mt-10">
            {isLoggedIn ? (<Link href="/projects" data-cta="go_to_projects" onClick={handleLinkClick} className="group flex w-full items-center justify-center gap-2 rounded-full bg-white px-6 py-4 text-lg font-semibold text-black transition-all duration-300 hover:scale-[1.02] hover:bg-white/95 active:scale-[0.98]">
                {t("a1Olpu")}
                <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1"/>
              </Link>) : (<button data-cta={isBarEntry ? 'bar_nav_get_started' : hasSignupAccess ? 'sign_up' : 'request_access'} onClick={handleDemoClick} className="group relative flex w-full cursor-pointer items-center justify-center gap-2 overflow-hidden rounded-full border-0 bg-white px-6 py-4 text-lg font-semibold text-black transition-all duration-300 hover:scale-[1.02] hover:bg-white/95 active:scale-[0.98]">
                <span className="relative z-10 flex items-center gap-2">
                  <span>{isBarEntry ? t("_aBLH2") : hasSignupAccess ? t("8HJxXG") : t("UnR8YN")}</span>
                  <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1"/>
                </span>
                {rippleElements}
              </button>)}
          </div>
        </nav>
      </div>

      { /* Sign Up Modal */ }
      <SignUpModal isOpen={showSignUpModal} onClose={()=>setShowSignUpModal(false)} returnPath={returnPath} defaultMode={modalMode} variant="marketing" source="nav"/>
    </div>);
}
